import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import {
  GetManagedByEmployeesByVaultIdV2OrderProperty,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import {
  IGetEmployeeAccountsManagedByEmployeesByVaultIdParams,
  TGetEmployeeAccountsManagedByEmployeesByVaultIdMethod,
} from '../../useEmployeeAccountsAPI/interfaces';
import {
  IOrganizationLoginEmployeesToEmployeesTableRow,
} from '../../../components/tables/OrganizationLoginEmployeesToEmployeesTable';
import useEmployeeAccountsAPI from '../../useEmployeeAccountsAPI';

export const REACT_QUERY_ORGANIZATION_LOGIN_EMPLOYEES_TO_EMPLOYEES_KEY = [
  ReactQueryKeyEnum.OrganizationLoginEmployeesToEmployees,
];

interface IPersistentFilters extends Pick<
  IGetEmployeeAccountsManagedByEmployeesByVaultIdParams, 'vaultId'
> {}

export interface IUseOrganizationLoginEmployeesToEmployeesTableParams {
  persistentFilters: IPersistentFilters;
}

export interface IUseOrganizationLoginEmployeesToEmployeesTableReturn extends
  Omit<
    IUseTableReturn<IOrganizationLoginEmployeesToEmployeesTableRow>,
    'selectedRows' | 'resetTableMethods'
  >,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage'>,
  Omit<IUseQueryTableFetchReturn<
    TGetEmployeeAccountsManagedByEmployeesByVaultIdMethod
  >, 'data' | 'isFetchedAfterMount'>
{
  employees: IUseQueryTableFetchReturn<
    TGetEmployeeAccountsManagedByEmployeesByVaultIdMethod
  >['data'];
  selectedEmployees: IUseTableReturn<
    IOrganizationLoginEmployeesToEmployeesTableRow
  >['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetEmployeeAccountsManagedByEmployeesByVaultIdParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<
    IGetEmployeeAccountsManagedByEmployeesByVaultIdParams['searchQuery']>
  >;
}

interface ITablePreservationConfigs extends
  Omit<IGetEmployeeAccountsManagedByEmployeesByVaultIdParams,
    'page' | 'pageLength' | 'vaultId' | 'orderPropertyName' | 'isDescending'
    | 'minUniqkeyAccessScore' | 'maxUniqkeyAccessScore' | 'isUniqkeyAccessScoreEmpty'
  >,
  Pick<
    IUseTableReturn<IOrganizationLoginEmployeesToEmployeesTableRow>,
    'columnOrderBy' | 'columnOrderDirection'
  >
{
  activePage: IGetEmployeeAccountsManagedByEmployeesByVaultIdParams['page'];
  perPage: IGetEmployeeAccountsManagedByEmployeesByVaultIdParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: GetManagedByEmployeesByVaultIdV2OrderProperty },
  ),
  columnOrderDirection: true,
};

const useOrganizationLoginEmployeesToEmployeesTable = (
  params: IUseOrganizationLoginEmployeesToEmployeesTableParams,
): IUseOrganizationLoginEmployeesToEmployeesTableReturn => {
  const {
    persistentFilters,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getEmployeeAccountsManagedByEmployeesByVaultId } = useEmployeeAccountsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetEmployeeAccountsManagedByEmployeesByVaultIdParams['searchQuery']
  >(() => initialSearchQuery);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedEmployees,
    resetTableMethods,
    ...restTableProps
  } = useTable<IOrganizationLoginEmployeesToEmployeesTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: employees, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_ORGANIZATION_LOGIN_EMPLOYEES_TO_EMPLOYEES_KEY,
    queryClient,
    request: getEmployeeAccountsManagedByEmployeesByVaultId,
    params: {
      page: activePage,
      pageLength: perPage,
      searchQuery,
      orderPropertyName: GetManagedByEmployeesByVaultIdV2OrderProperty[
        upperFirst(columnOrderBy) as keyof typeof GetManagedByEmployeesByVaultIdV2OrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
  });

  return {
    employees,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedEmployees,
    localization,
    searchQuery,
    setSearchQuery,
    resetActivePage,
    ...restTableProps,
  };
};

export default useOrganizationLoginEmployeesToEmployeesTable;
